import React, { useEffect, useState } from 'react';
import { useDispatch, useStore } from 'react-redux';
import {
  maxWidthContainer,
  maxWidthContainerWithoutGutters,
} from '@noths/polaris-client-ribbons-base';
import { LazyRender, PartnerPageHeader } from '@noths/polaris-client-ribbons-design-system';
import { visuallyHidden } from '@noths/polaris-client-styles';

import { PartnerLegals } from 'src/components/molecules/PartnerLegals/PartnerLegals';
import { Tabs } from 'src/components/molecules/Tabs/Tabs';
import { RecommendedProductsCarouselContainer } from 'src/components/organisms/RecommendedProductsCarouselContainer/RecommendedProductsCarouselContainer';
import type { ReduxApplicationState } from 'src/redux/combinedReducer';
import type { PartnerContentState } from 'src/redux/content/types';
import { actions as navigationActions } from 'src/redux/navigation/slice';
import type { PartnerInsightsState } from 'src/redux/partnerInsights/types';
import * as styles from './PartnerLayout.styles';

export interface PartnerTab {
  active?: boolean;
  content: React.ReactNode;
  href: string;
  label: string;
}

export interface PartnerLayoutProps {
  pageLiveRegionText: string;
  partnerContent: PartnerContentState;
  partnerInsights?: PartnerInsightsState;
  tabs: PartnerTab[];
}
const RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN = '200px';

export const PartnerLayout = ({
  pageLiveRegionText,
  partnerContent,
  partnerInsights,
  tabs,
}: PartnerLayoutProps) => {
  const dispatch = useDispatch();
  const store = useStore<ReduxApplicationState>();
  const [currentHash, setCurrentHash] = useState('');

  // Sync hash to Redux when it changes (e.g. tab click) so pagination and URL updates preserve it.
  // Only dispatch when the hash actually changed to avoid redundant Redux updates and re-renders.
  useEffect(() => {
    const syncHash = () => {
      const hash = window.location.hash;
      setCurrentHash(hash);
      if (store.getState().navigation.hash !== hash) {
        dispatch(navigationActions.setHash(hash));
      }
    };

    syncHash();
    window.addEventListener('hashchange', syncHash);

    return () => {
      window.removeEventListener('hashchange', syncHash);
    };
  }, [dispatch, store]);

  // Enhance tabs with section wrappers and active state based on hash
  const enhancedTabs = tabs.map((tab) => {
    const isActive = currentHash ? currentHash === tab.href : tab.href === tabs[0]?.href;
    return {
      ...tab,
      active: tab.active ?? isActive,
      content: <section id={tab.href.replace('#', '')}>{tab.content}</section>,
    };
  });

  return (
    <>
      {pageLiveRegionText && (
        <div css={visuallyHidden} role="status">
          {pageLiveRegionText}
        </div>
      )}
      <div css={styles.partnerPageHeaderWrapper}>
        <div css={maxWidthContainerWithoutGutters}>
          <PartnerPageHeader
            coverImageSrc={partnerContent.coverImage?.src ?? ''}
            introText={partnerContent.shortBrandBio ?? ''}
            location={partnerContent.location ?? ''}
            logoImageSrc={partnerContent.logo?.src ?? ''}
            name={partnerContent.name ?? ''}
            numberOfOrders={partnerInsights?.numberOfSales}
            rating={partnerInsights?.averageProductRating ?? undefined}
            startDate={partnerContent.startDate}
            topBrand={partnerInsights?.topBrand}
          />
        </div>
      </div>
      <div css={maxWidthContainer}>
        <div css={styles.carouselContainer}>
          <LazyRender
            options={{
              rootMargin: RECOMMENDED_PRODUCTS_LAZY_LOAD_MARGIN,
            }}
          >
            <RecommendedProductsCarouselContainer
              placementType="item_page.same_seller2"
              title="Our bestsellers"
            />
          </LazyRender>
        </div>
        <Tabs ariaLabel="Partner sections" tabs={enhancedTabs} />
      </div>
      <div css={maxWidthContainerWithoutGutters}>
        <PartnerLegals
          returnAddress={partnerContent.returnsAddress}
          vatNumber={partnerContent.vatNumber}
        />
      </div>
    </>
  );
};
